import {
  defaultIntentFor,
  deriveStopBilling,
  kindOf,
  roleOf,
  sumChargedLegs,
  type RouteStopKind,
  type RouteStopKindInput,
  type StopBilling,
} from '@/services/api/routeStopKind';
import type {
  RoutePlanEndpoint,
  RoutePlanLegSource,
} from '@/services/api/routePlans';
import type {
  UjpRouteLeg,
  UjpRoutePayload,
  UjpRouteStop,
  UjpStopRole,
} from '@/services/api/ujp';

/**
 * Pure (React-free) model + helpers behind the UJP wizard: the route draft the
 * wizard carries (CR-D2 / CR-D9), the charged-km rule (CR-D7), and the mapping
 * to the `route:{stops, legs}` payload the service expects. Kept out of the
 * components so the risk-bearing bits — re-sequencing on reorder / remove,
 * which legs count as "pool legs", the payload shape — are unit-tested without
 * driving a modal.
 *
 * CR-6 (§22): a stop is `type` + `intent` + `pool`; the legacy `role` is
 * derived (`roleOf`) only for the wire.
 *
 * CR-8b: the pool is the FIRST and LAST stop by position; `charged` (null =
 * follow the client rule) is meaningful on those two edge stops only, and the
 * km rule derives pool + billing from position (`routeStopBilling`). The
 * `pool` a row carries is the SERVER ECHO of the picked plan and is what the
 * chips display; `kmCharged` counts a leg when BOTH endpoints are billed
 * (D4), which is also the server's `kmProposedDefault`.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Route draft
// ─────────────────────────────────────────────────────────────────────────────

export interface RouteRowDraft extends RouteStopKind {
  uid: string;
  /** CR-8b: billing override, meaningful on the first / last stop; `null` = follow the client rule. */
  charged: boolean | null;
  name: string;
  address: string;
  latitude?: number;
  longitude?: number;
  /**
   * R3 manual mode: the lane linkage carried by the inline RoutePlanBuilder so a
   * lane-picked stop keeps its master link across a manual↔planner toggle. Only
   * meaningful while editing; the UJP wire payload does not store them.
   */
  addressId?: string | null;
  endpoint?: RoutePlanEndpoint | null;
  /** CR-5: workflow pinned on the picked route plan (read-only in the wizard). */
  workflowId?: string | null;
  workflowName?: string | null;
}

export interface RouteLegDraft {
  fromUid: string;
  toUid: string;
  /** Canonical decimal string (`"84.6"`); `""` = unknown. */
  km: string;
  /** Typed by ops — the map never overwrites a manual km. */
  manual: boolean;
  /** R3 manual mode: how the km was measured (LANE/Directions/Haversine) — for
   *  the inline builder's source badge. Null when typed/unknown. */
  source?: RoutePlanLegSource | null;
}

export interface RouteDraft {
  stops: RouteRowDraft[];
  legs: RouteLegDraft[];
}

let uidCounter = 0;
const nextUid = () => `rstop-${++uidCounter}`;

/**
 * A fresh row. `kind` may carry the new keys, a legacy `role`, or both — it is
 * normalised through `kindOf`.
 */
export const makeRouteRow = (
  kind: RouteStopKindInput,
  patch: Partial<Omit<RouteRowDraft, keyof RouteStopKind>> = {}
): RouteRowDraft => {
  const k = kindOf(kind);
  return {
    uid: nextUid(),
    ...k,
    charged: typeof kind.charged === 'boolean' ? kind.charged : null,
    name: '',
    address: '',
    ...patch,
  };
};

/** The legacy role of a row — for the wire only. */
export const rowRole = (row: RouteStopKind): UjpStopRole => roleOf(row);

/**
 * CR-8b: per stop, in order, `{ isEdge, effectiveCharged, pool, override }`
 * derived from position + the edge override with the client rule as default
 * (see `deriveStopBilling`). The one source for the km rule and the per-stop
 * "ditagih / tidak ditagih" hints.
 */
export const routeStopBilling = (
  stops: ReadonlyArray<{ charged?: boolean | null }>,
  clientDefault: boolean
): StopBilling[] => deriveStopBilling(stops, clientDefault);

/** CR-8b: the effective billing of the stop at `index`. */
export const stopCharged = (
  draft: Pick<RouteDraft, 'stops'>,
  index: number,
  clientDefault: boolean
): boolean =>
  routeStopBilling(draft.stops, clientDefault)[index]?.effectiveCharged ?? true;

/** CR-8 (D3): an edge stop on the route overrides the client rule. */
export const routeChargedOverride = (
  draft: Pick<RouteDraft, 'stops'>,
  clientDefault: boolean
): boolean => routeStopBilling(draft.stops, clientDefault).some((b) => b.override);

/**
 * CR-8: "some pool leg is billed" — the local mirror of the estimate's
 * `chargedPositioning`: an edge stop (the positioning / return leg) is billed.
 * The client rule when the route has no stops yet.
 */
export const routeAnyPoolCharged = (
  draft: Pick<RouteDraft, 'stops'>,
  clientDefault: boolean
): boolean => {
  const edges = routeStopBilling(draft.stops, clientDefault).filter((b) => b.isEdge);
  if (edges.length === 0) return clientDefault;
  return edges.some((b) => b.effectiveCharged);
};

/**
 * Rebuild the leg list for the CURRENT consecutive stop pairs, keeping the km
 * (and its manual flag) of any pair that already existed. Runs after every
 * reorder / add / remove so a leg never points at a stop that moved away.
 */
export const resequenceLegs = (
  stops: RouteRowDraft[],
  prev: RouteLegDraft[]
): RouteLegDraft[] => {
  const byPair = new Map(prev.map((l) => [`${l.fromUid}>${l.toUid}`, l]));
  const out: RouteLegDraft[] = [];
  for (let i = 0; i < stops.length - 1; i++) {
    const fromUid = stops[i].uid;
    const toUid = stops[i + 1].uid;
    out.push(
      byPair.get(`${fromUid}>${toUid}`) ?? {
        fromUid,
        toUid,
        km: '',
        manual: false,
      }
    );
  }
  return out;
};

/** The trucking loop ops start from: pool → pickup → drop → pool (CR-6 vocabulary). */
export const defaultRouteDraft = (): RouteDraft => {
  const stops = [
    makeRouteRow({ type: 'PICKUP', pool: true }),
    makeRouteRow({ type: 'PICKUP' }),
    makeRouteRow({ type: 'DROP_OFF', intent: 'UNLOAD' }),
    makeRouteRow({ type: 'DROP_OFF', intent: 'GENERIC', pool: true }),
  ];
  return { stops, legs: resequenceLegs(stops, []) };
};

export const emptyRouteDraft = (): RouteDraft => ({ stops: [], legs: [] });

export const addRouteRow = (
  draft: RouteDraft,
  kind: RouteStopKindInput = { type: 'DROP_OFF' }
): RouteDraft => {
  const stops = [...draft.stops, makeRouteRow(kind)];
  return { stops, legs: resequenceLegs(stops, draft.legs) };
};

export const removeRouteRow = (draft: RouteDraft, uid: string): RouteDraft => {
  const stops = draft.stops.filter((s) => s.uid !== uid);
  return { stops, legs: resequenceLegs(stops, draft.legs) };
};

export const moveRouteRow = (
  draft: RouteDraft,
  uid: string,
  direction: -1 | 1
): RouteDraft => {
  const index = draft.stops.findIndex((s) => s.uid === uid);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= draft.stops.length) return draft;
  const stops = [...draft.stops];
  [stops[index], stops[target]] = [stops[target], stops[index]];
  return { stops, legs: resequenceLegs(stops, draft.legs) };
};

export const patchRouteRow = (
  draft: RouteDraft,
  uid: string,
  patch: Partial<RouteRowDraft>
): RouteDraft => ({
  ...draft,
  stops: draft.stops.map((s, i) => {
    if (s.uid !== uid) return s;
    const next: RouteRowDraft = { ...s, ...patch };
    // Keep the kind consistent (same rules as the planner, CR6-D4 / CR-8b): a
    // type change re-defaults the intent — GENERIC on the last stop (the pool
    // return), UNLOAD elsewhere. `charged` is stored as given; position decides
    // whether it counts.
    const typeChanged = patch.type !== undefined && patch.type !== s.type;
    const isLast = i === draft.stops.length - 1;
    if (patch.intent === undefined && typeChanged) {
      next.intent = defaultIntentFor(next.type, isLast);
    }
    if (next.type === 'PICKUP') next.intent = 'GENERIC';
    return next;
  }),
});

/** Ops typed a km → manual, frozen against the map. */
export const setLegKm = (
  draft: RouteDraft,
  fromUid: string,
  toUid: string,
  km: string,
  manual = true
): RouteDraft => ({
  ...draft,
  legs: draft.legs.map((l) =>
    l.fromUid === fromUid && l.toUid === toUid ? { ...l, km, manual } : l
  ),
});

export const isRowLocated = (row: RouteRowDraft): boolean =>
  row.latitude != null &&
  row.longitude != null &&
  Number.isFinite(row.latitude) &&
  Number.isFinite(row.longitude);

const legKmNumber = (leg: RouteLegDraft): number => {
  const n = Number(leg.km);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const round1 = (n: number) => Math.round(n * 10) / 10;

/** Σ every leg. */
export const kmAllLegs = (draft: RouteDraft): number =>
  round1(draft.legs.reduce((sum, l) => sum + legKmNumber(l), 0));

/**
 * Σ legs whose two endpoints are both billed (CR-D7 / CR6-D3 / CR8-D4
 * `km_charged`) under the CR-8b derivation: a middle stop always is, an edge
 * stop iff `charged ?? clientDefault` — the same rule the planner applies and
 * `UjpCostService` runs, so the wizard's number matches the server's.
 */
export const kmCharged = (draft: RouteDraft, clientDefault = false): number =>
  round1(
    sumChargedLegs(
      draft.stops,
      draft.legs.map((l) => ({ from: l.fromUid, to: l.toUid, km: legKmNumber(l) })),
      (s) => s.uid,
      clientDefault
    )
  );

/**
 * `km_yang_diajukan` default = the server's `kmProposedDefault`, which since
 * CR8-D4 IS the charged km under the per-stop rule (no more "all legs when the
 * client is charged" switch: a charged client's pool legs are simply billed).
 */
export const defaultKmProposed = (
  draft: RouteDraft,
  chargedPositioning: boolean
): number => kmCharged(draft, chargedPositioning);

/**
 * The route ends in a pool DROP_OFF — the truck goes back to the pool after the
 * last drop (CR6-D3; was "has a RETURN stop"). Drives the reverse pre-tick.
 */
export const endsInPoolDrop = (
  stops: ReadonlyArray<RouteStopKindInput>
): boolean => {
  const last = stops[stops.length - 1];
  if (!last) return false;
  const kind = kindOf(last);
  return kind.type === 'DROP_OFF' && kind.pool;
};

/** Field-level problems of a route draft; empty when it is submittable. */
export const routeDraftErrors = (draft: RouteDraft): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (draft.stops.length < 2) errors.route = 'Rute minimal dua stop dengan lokasi.';
  draft.stops.forEach((s, i) => {
    if (!s.name.trim()) errors[`${s.uid}.name`] = 'Nama tempat wajib diisi';
    if (!s.address.trim() || !isRowLocated(s))
      errors[`${s.uid}.address`] = `Stop ${i + 1} belum punya lokasi`;
  });
  draft.legs.forEach((l) => {
    if (legKmNumber(l) <= 0)
      errors[`${l.fromUid}>${l.toUid}.km`] = 'Km leg wajib > 0';
  });
  return errors;
};

// ─────────────────────────────────────────────────────────────────────────────
// Payload ↔ saved route
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Draft → `route:{stops, legs}` (1-based seq; km as decimal STRINGS — the wire
 * contract). Each stop carries `type`/`intent`/`pool` (the server echo of the
 * picked plan) plus the derived `role` for a release (CR6-D5); the first and
 * last stop also send their CR-8b `charged` override (null = follow the
 * client rule), middle stops omit the key — their billing is not negotiable.
 */
export const toRoutePayload = (draft: RouteDraft): UjpRoutePayload => {
  const seqOf = new Map(draft.stops.map((s, i) => [s.uid, i + 1]));
  const last = draft.stops.length - 1;
  const stops: UjpRouteStop[] = draft.stops.map((s, i) => ({
    seq: i + 1,
    type: s.type,
    intent: s.intent,
    pool: s.pool,
    ...(i === 0 || i === last ? { charged: s.charged ?? null } : {}),
    role: rowRole(s),
    name: s.name.trim(),
    address: s.address.trim(),
    latitude: s.latitude ?? 0,
    longitude: s.longitude ?? 0,
    // CR-5: the plan's pin rides along into the UJP snapshot (approve passes it
    // to the shipment with allowWorkflowFallback on the server side).
    ...(s.workflowId
      ? { workflowId: s.workflowId, workflowName: s.workflowName ?? null }
      : {}),
  }));
  const legs: UjpRouteLeg[] = draft.legs.map((l, i) => ({
    seq: i + 1,
    fromSeq: seqOf.get(l.fromUid) ?? i + 1,
    toSeq: seqOf.get(l.toUid) ?? i + 2,
    km: String(legKmNumber(l)),
  }));
  return { stops, legs };
};

/**
 * A route plan (or a UJP's route snapshot) → an editable draft: legs keep their
 * km, marked manual so nothing rewrites a saved distance. Typed structurally so
 * both `RoutePlan` (extra `addressId`/`source`/`edited` fields) and a UJP
 * detail's `route` satisfy it. Stops read through `kindOf`, so a pre-CR-6
 * snapshot (role only) restores as before; each stop's CR-8 `charged` rides
 * along (via `makeRouteRow`) so edit mode resubmits the same override, and
 * the km rule derives what counts from position (CR-8b).
 */
export const fromSavedRoute = (route: {
  stops: Array<
    RouteStopKindInput & {
      seq: number;
      name: string;
      address: string;
      latitude: number;
      longitude: number;
      workflowId?: string | null;
      workflowName?: string | null;
    }
  >;
  legs: Array<{ seq: number; fromSeq: number; toSeq: number; km: string | number }>;
}): RouteDraft => {
  const sorted = [...route.stops].sort((a, b) => a.seq - b.seq);
  const rows = sorted.map((s) =>
    makeRouteRow(s, {
      name: s.name,
      address: s.address,
      latitude: s.latitude,
      longitude: s.longitude,
      workflowId: s.workflowId ?? null,
      workflowName: s.workflowId ? (s.workflowName ?? null) : null,
    })
  );
  const uidBySeq = new Map(sorted.map((s, i) => [s.seq, rows[i].uid]));
  const base = resequenceLegs(rows, []);
  const legs = base.map((l) => {
    const saved = route.legs.find(
      (x) => uidBySeq.get(x.fromSeq) === l.fromUid && uidBySeq.get(x.toSeq) === l.toUid
    );
    const savedKm = saved ? Number(saved.km) : 0;
    return saved && Number.isFinite(savedKm) && savedKm > 0
      ? { ...l, km: String(savedKm), manual: true }
      : l;
  });
  return { stops: rows, legs };
};
